import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Upload, Image as ImageIcon } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { compressImage } from '../../utils/imageCompressor';

const TenantsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
    address: '',
    phone: '',
    email: '',
    bank_name: 'Banco Nacional',
    bank_account: '',
    bank_account_type: 'Corriente',
    bank_account_holder: '',
    logo_url: null,
    loyalty_enabled: true,
    tax_enabled: true
  });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`, {
        headers: getAuthHeaders()
      });
      setTenants(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar tenants'));
    }
  };

  const handleOpenDialog = () => {
    setEditingTenant(null);
    setFormData({ 
      name: '', 
      admin_email: '', 
      admin_password: '', 
      admin_name: '',
      address: '',
      phone: '',
      email: '',
      bank_name: 'Banco Nacional',
      bank_account: '',
      bank_account_type: 'Corriente',
      bank_account_holder: '',
      logo_url: null,
      loyalty_enabled: true,
      tax_enabled: true
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      address: tenant.address || '',
      phone: tenant.phone || '',
      email: tenant.email || '',
      bank_name: tenant.bank_name || 'Banco Nacional',
      bank_account: tenant.bank_account || '',
      bank_account_type: tenant.bank_account_type || 'Corriente',
      bank_account_holder: tenant.bank_account_holder || tenant.name,
      logo_url: tenant.logo_url || null,
      loyalty_enabled: tenant.loyalty_enabled !== false,
      tax_enabled: tenant.tax_enabled !== false,
      admin_email: '',
      admin_password: '',
      admin_name: ''
    });
    setIsDialogOpen(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen');
      return;
    }

    setUploadingLogo(true);
    try {
      // Comprimir en el navegador (redimension a 800x800 + WebP): se acepta
      // cualquier imagen sin tope de tamano porque llega ya optimizada.
      const compressedFile = await compressImage(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await axios.post(`${API}/tenants/upload-logo`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, logo_url: response.data.logo_url });
      toast.success('Logo subido exitosamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al subir logo'));
    }
    setUploadingLogo(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingTenant) {
        // When editing, send all tenant data including bank info
        const updateData = {
          name: formData.name,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          bank_name: formData.bank_name,
          bank_account: formData.bank_account,
          bank_account_type: formData.bank_account_type,
          bank_account_holder: formData.bank_account_holder || formData.name,
          logo_url: formData.logo_url,
          loyalty_enabled: formData.loyalty_enabled,
          tax_enabled: formData.tax_enabled
        };
        
        await axios.put(`${API}/tenants/${editingTenant.id}`, updateData, {
          headers: getAuthHeaders()
        });
        toast.success('Tenant actualizado exitosamente');
      } else {
        // When creating, include admin info
        const tenantData = {
          name: formData.name,
          address: formData.address,
          phone: formData.phone,
          email: formData.email || formData.admin_email,
          admin_email: formData.admin_email,
          admin_password: formData.admin_password,
          admin_name: formData.admin_name,
          bank_name: formData.bank_name,
          bank_account: formData.bank_account,
          bank_account_type: formData.bank_account_type,
          bank_account_holder: formData.bank_account_holder || formData.name,
          logo_url: formData.logo_url,
          loyalty_enabled: formData.loyalty_enabled,
          tax_enabled: formData.tax_enabled
        };
        
        await axios.post(`${API}/tenants`, tenantData, {
          headers: getAuthHeaders()
        });
        toast.success('Tenant creado exitosamente');
      }

      await loadTenants();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar tenant'));
    }
    setLoading(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingTenant(null);
      setFormData({ 
        name: '', 
        admin_email: '', 
        admin_password: '', 
        admin_name: '',
        address: '',
        phone: '',
        email: '',
        bank_name: 'Banco Nacional',
        bank_account: '',
        bank_account_type: 'Corriente',
        bank_account_holder: '',
        logo_url: null,
        loyalty_enabled: true,
        tax_enabled: true
      });
    }, 100);
  };

  const handleToggleStatus = async (tenant) => {
    try {
      await axios.patch(
        `${API}/tenants/${tenant.id}/toggle-status`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Tenant ${tenant.is_active ? 'desactivado' : 'activado'} exitosamente`);
      loadTenants();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar estado'));
    }
  };

  const handleDelete = async (tenantId) => {
    if (!window.confirm('¿Estás seguro de eliminar este tenant? Esto también eliminará todos los usuarios y datos asociados.')) return;

    try {
      await axios.delete(`${API}/tenants/${tenantId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Tenant eliminado exitosamente');
      loadTenants();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar tenant'));
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Gestión de Tenants</h2>
            <p className="text-sm text-gray-600">Administra los restaurantes del sistema</p>
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleOpenDialog} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <DialogHeader>
              <DialogTitle>{editingTenant ? 'Editar Tenant' : 'Nuevo Tenant'}</DialogTitle>
              <DialogDescription>
                {editingTenant
                  ? 'Modifica los datos del restaurante y su configuración'
                  : 'Registra un nuevo restaurante en el sistema con su administrador'}
              </DialogDescription>
            </DialogHeader>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nombre del Restaurante</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Restaurante La Esquina"
                  required
                  className="rounded-xl"
                />
              </div>

              {/* Logo Upload Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4" />
                  Logo del Restaurante (para facturas)
                </Label>
                <div className="flex items-center gap-4">
                  {formData.logo_url ? (
                    <div className="relative">
                      <img 
                        src={getImageUrl(formData.logo_url)}
                        alt="Logo" 
                        className="w-24 h-24 object-contain border rounded-lg p-2 bg-white"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData({ ...formData, logo_url: null })}
                        className="absolute -top-2 -right-2 rounded-full w-6 h-6 p-0"
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="rounded-xl"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">
                      Formatos: JPG, PNG, GIF (máx. 2MB)
                    </p>
                  </div>
                </div>
              </div>

              {!editingTenant && (
                <>
                  <div>
                    <Label>Nombre del Administrador</Label>
                    <Input
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      placeholder="Ej: Juan Pérez"
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Email del Administrador</Label>
                    <Input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      placeholder="admin@restaurante.com"
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Contraseña del Administrador</Label>
                    <Input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      placeholder="Contraseña segura"
                      required
                      minLength={8}
                      className="rounded-xl"
                    />
                  </div>
                </>
              )}

              {/* Contact Information */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Información de Contacto</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Dirección</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Dirección del restaurante"
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Teléfono de contacto"
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contacto@restaurante.com"
                      required
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Datos Bancarios (Para Transferencias)
                </h3>
                <div className="space-y-3 bg-blue-50 p-4 rounded-xl">
                  <div>
                    <Label>Nombre del Banco</Label>
                    <Input
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="Ej: Banco Nacional, Banco Popular"
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Número de Cuenta</Label>
                    <Input
                      value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                      placeholder="1234-5678-9012-3456"
                      className="rounded-xl font-mono"
                    />
                  </div>
                  <div>
                    <Label>Tipo de Cuenta</Label>
                    <select
                      value={formData.bank_account_type}
                      onChange={(e) => setFormData({ ...formData, bank_account_type: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 p-2"
                    >
                      <option value="Corriente">Corriente</option>
                      <option value="Ahorros">Ahorros</option>
                    </select>
                  </div>
                  <div>
                    <Label>Titular de la Cuenta</Label>
                    <Input
                      value={formData.bank_account_holder}
                      onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                      placeholder="Nombre del titular"
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Configuración del Sistema</h3>
                <div className="space-y-3 bg-purple-50 p-4 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Programa de Fidelización</Label>
                      <p className="text-xs text-gray-500">Permite acumular puntos por compras</p>
                    </div>
                    <Switch
                      checked={formData.loyalty_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, loyalty_enabled: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Cálculo de Impuestos</Label>
                      <p className="text-xs text-gray-500">Aplica impuestos (ITBIS) a las órdenes</p>
                    </div>
                    <Switch
                      checked={formData.tax_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, tax_enabled: checked })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 rounded-xl">
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                  {loading ? 'Guardando...' : (editingTenant ? 'Actualizar' : 'Crear')}
                </Button>
              </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {tenants.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No hay tenants registrados</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tenants.map((tenant) => (
            <Card key={tenant.id} className="p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  {tenant.logo_url ? (
                    <div className="w-12 h-12 border rounded-lg flex items-center justify-center bg-white overflow-hidden">
                      <img 
                        src={getImageUrl(tenant.logo_url)}
                        alt={tenant.name}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-400 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">{tenant.name}</h3>
                    <p className="text-sm text-gray-500">ID: {tenant.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{tenant.is_active ? 'Activo' : 'Inactivo'}</Label>
                    <Switch
                      checked={tenant.is_active}
                      onCheckedChange={() => handleToggleStatus(tenant)}
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(tenant)} className="rounded-xl">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(tenant.id)} className="rounded-xl text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantsTab;
